import {AfterViewInit, Component, Input, ViewChild} from '@angular/core';
import {faDownload, faFileLines} from "@fortawesome/free-solid-svg-icons";
import {MatTableDataSource} from "@angular/material/table";
import {MatSort} from "@angular/material/sort";
import {AccomplishmentService} from "../../../../services/accomplishment.service";
import {forkJoin} from "rxjs";
import {AssessmentService} from "../../../../services/assessment.service";
import {ExportAuthActivitiesComponent} from "../export-auth-activities/export-auth-activities.component";
import {MatDialog} from "@angular/material/dialog";

export interface Filters {
  calendarYear: number | null;
  fiscalYear: number | null;
  region: string | null;
  forestName: string | null;
  treatmentOptionId: number | null;
}

interface TreatmentOption {
  id: number;
  label: string;
  unitOfMeasure: string;
}

interface AssessmentMeta {
  assessmentId: number;
  region: string | null;
  forestName: string | null;
  calendarYear: number | null;
  fiscalYear: number | null;
}

interface BaerTreatmentRow {
  treatmentOptionId: number;
  treatmentCode: string;
  treatmentName: string;
  unitOfMeasure: string;
  unitsAuthorized: {
    calendarYear: number;
    fiscalYear: number;
  };
  unitsAccomplished: {
    calendarYear: number;
    fiscalYear: number;
  };
}


@Component({
  selector: 'app-authorized-activities',
  templateUrl: './authorized-activities.component.html',
  styleUrl: './authorized-activities.component.scss',
  standalone: false
})
export class AuthorizedActivitiesComponent implements AfterViewInit {
  @Input() user: any;

  @ViewChild(MatSort) sort: MatSort = new MatSort;

  summaryData: MatTableDataSource<BaerTreatmentRow> =
    new MatTableDataSource<BaerTreatmentRow>();
  allSummaryDataRows: BaerTreatmentRow[] = [];
  displayedColumns: string[] = [
    'treatment',
    'units',
    'calendarYearAuthorized',
    'fiscalYearAuthorized',
    'calendarYearAccomplished',
    'fiscalYearAccomplished',
  ];

  private allTreatments: any[] = [];

  private assessmentMeta = new Map<number, AssessmentMeta>();

  calendarYears: number[] = [];
  fiscalYears: number[] = [];
  regions: string[] = [];
  forestNames: string[] = [];
  treatmentOptions: TreatmentOption[] = [];

  filters: Filters = {
    calendarYear: null,
    fiscalYear: null,
    region: null,
    forestName: null,
    treatmentOptionId: null,
  };

  loading = false;

  constructor(
    private readonly accomplishmentService: AccomplishmentService,
    private readonly assessmentService: AssessmentService,
    public dialog: MatDialog,
  ) {
  }

  ngAfterViewInit(): void {
    this.loadAllData();
    this.summaryData.sortingDataAccessor = (row: any, columnId: string) => {
      switch (columnId) {
        case 'treatment':
          return row.treatmentName ?? '';
        case 'units':
          return row.unitOfMeasure ?? '';
        case 'calendarYearAuthorized':
          return Number(row.unitsAuthorized?.calendarYear ?? 0);
        case 'fiscalYearAuthorized':
          return Number(row.unitsAuthorized?.fiscalYear ?? 0);
        case 'calendarYearAccomplished':
          return Number(row.unitsAccomplished?.calendarYear ?? 0);
        case 'fiscalYearAccomplished':
          return Number(row.unitsAccomplished?.fiscalYear ?? 0);
        default:
          return row[columnId] ?? '';
      }
    };
  }

  private loadAllData(): void {
    this.loading = true;

    const pageNumber = 1;
    const pageSize = 1000;
    const emptyFilters: any = {};
    const sort = 'updated_on';
    const sortDirection = 'desc';

    forkJoin({
      treatments: this.accomplishmentService.getAccomplishmentTreatment(
        'forest_treatment.treatment_option,forest_treatment.treatment_specifications,accomplishment'
      ),
      assessments: this.assessmentService.getAssessmentList(
        pageNumber,
        pageSize,
        emptyFilters,
        sort,
        sortDirection,
        false
      ),
    }).subscribe({
      next: ({treatments, assessments}: any) => {
        this.allTreatments =
          treatments?.accomplishmentTreatments ??
          treatments?.data ??
          treatments ??
          [];

        const assessmentArray = assessments?.assessments ?? assessments ?? [];
        this.buildAssessmentMeta(assessmentArray);

        this.buildFilterOptions(this.allTreatments);

        this.applyFilters();

        this.summaryData.sort = this.sort;
        this.loading = false;
      },
      error: (err: any) => {
        console.error('Failed to load data for authorized activities', err);
        this.allTreatments = [];
        this.summaryData.data = [];
        this.allSummaryDataRows = [];
        this.loading = false;
      },
    });
  }

  private buildAssessmentMeta(assessments: any[]): void {
    this.assessmentMeta.clear();
    for (const a of assessments) {
      const dataStr: string | null = a.fire_discovery_date ?? null;
      let calendarYear: null | number = null;
      let fiscalYear: null | number = null;
      if (dataStr) {
        const d = new Date(dataStr);
        if (!Number.isNaN(d.valueOf())) {
          calendarYear = d.getFullYear();
          fiscalYear = this.getFiscalYear(d);
        }
      }
      const meta: AssessmentMeta = {
        assessmentId: a.assessment_id,
        region: a.region ?? null,
        forestName: a.forest_name ?? null,
        calendarYear,
        fiscalYear,
      };

      if (meta.assessmentId != null) {
        this.assessmentMeta.set(meta.assessmentId, meta);
      }
    }
  }

  private buildFilterOptions(allData: any[]): void {
    const calendarYearSet = new Set<number>();
    const fiscalYearSet = new Set<number>();
    const regionSet = new Set<string>();
    const forestSet = new Set<string>();
    const treatmentMap = new Map<number, TreatmentOption>();

    for (const at of allData) {
      const ft = at.forest_treatment;
      const opt = ft?.treatment_option;

      const meta = this.assessmentMeta.get(at.accomplishment?.assessment_id);
      if (meta?.calendarYear != null) {
        calendarYearSet.add(meta.calendarYear);
      }
      if (meta?.fiscalYear != null) {
        fiscalYearSet.add(meta.fiscalYear);
      }
      if (meta?.region) {
        regionSet.add(meta.region);
      }
      if (meta?.forestName) {
        forestSet.add(meta.forestName);
      }

      if (opt && opt.treatment_option_id != null) {
        if (!treatmentMap.has(opt.treatment_option_id)) {
          treatmentMap.set(opt.treatment_option_id, {
            id: opt.treatment_option_id,
            label: opt.treatment_option,
            unitOfMeasure: opt.unit_of_measure,
          });
        }
      }
    }

    this.calendarYears = Array.from(calendarYearSet).sort(
      (a, b) => a - b
    );
    this.fiscalYears = Array.from(fiscalYearSet).sort(
      (a, b) => a - b
    );
    this.regions = Array.from(regionSet).sort((a, b) =>
      a.localeCompare(b)
    );
    this.forestNames = Array.from(forestSet).sort((a, b) =>
      a.localeCompare(b)
    );
    this.treatmentOptions = Array.from(treatmentMap.values()).sort(
      (a, b) => a.label.localeCompare(b.label)
    );
  }

  onCalendarYearChange(year: number | null): void {
    this.filters.calendarYear = year;
    this.applyFilters();
  }

  onFiscalYearChange(fy: number | null): void {
    this.filters.fiscalYear = fy;
    this.applyFilters();
  }

  onRegionChange(region: string | null): void {
    this.filters.region = region;
    this.applyFilters();
  }

  onForestChange(forestName: string | null): void {
    this.filters.forestName = forestName;
    this.applyFilters();
  }

  onTreatmentChange(optionId: number | null): void {
    this.filters.treatmentOptionId = optionId;
    this.applyFilters();
  }

  private applyFilters(): void {
    const filteredTreatments = this.filterAccomplishmentTreatments(
      this.allTreatments
    );
    this.allSummaryDataRows = this.buildBaerTreatmentRows(filteredTreatments);

    this.summaryData.data = this.allSummaryDataRows.slice(0, 10);
    if (this.sort) {
      this.summaryData.sort = this.sort;
    }
  }

  private filterAccomplishmentTreatments(allData: any[], filters: Filters = this.filters): any[] {
    const {
      calendarYear,
      fiscalYear,
      region,
      forestName,
      treatmentOptionId,
    } = filters;

    return allData.filter((at: any) => {
      const ft = at.forest_treatment;
      const opt = ft?.treatment_option;

      const meta = this.assessmentMeta.get(at.accomplishment?.assessment_id);

      let rowCalendarYear: number | null =
        meta?.calendarYear ?? null;
      let rowFiscalYear: number | null = meta?.fiscalYear ?? null;

      if ((calendarYear != null || fiscalYear != null) && at.date_completed) {
        const d = new Date(at.date_completed);
        if (!Number.isNaN(d.valueOf())) {
          if (rowCalendarYear == null) {
            rowCalendarYear = d.getFullYear();
          }
          if (rowFiscalYear == null) {
            rowFiscalYear = this.getFiscalYear(d);
          }
        }
      }

      if (calendarYear != null && rowCalendarYear !== calendarYear) {
        return false;
      }

      if (fiscalYear != null && rowFiscalYear !== fiscalYear) {
        return false;
      }

      if (region && meta?.region && meta.region !== region) {
        return false;
      }

      if (forestName && meta?.forestName && meta.forestName !== forestName) {
        return false;
      }

      if (
        treatmentOptionId != null &&
        opt?.treatment_option_id !== treatmentOptionId
      ) {
        return false;
      }

      return true;
    });
  }

  private buildBaerTreatmentRows(filteredData: any[]): BaerTreatmentRow[] {
    const map = new Map<number, BaerTreatmentRow>();

    for (const at of filteredData) {
      const ft = at.forest_treatment;
      const opt = ft?.treatment_option;
      if (!ft || !opt) continue;

      const key = opt.treatment_option_id;
      if (key == null) continue;

      let row = map.get(key);
      if (!row) {
        row = {
          treatmentOptionId: key,
          treatmentCode: opt.code,
          treatmentName: opt.treatment_option,
          unitOfMeasure: opt.unit_of_measure,
          unitsAuthorized: {
            calendarYear: 0,
            fiscalYear: 0,
          },
          unitsAccomplished: {
            calendarYear: 0,
            fiscalYear: 0,
          },
        };
        map.set(key, row);
      }

      const specs: any[] = ft.treatment_specifications ?? [];
      for (const spec of specs) {
        const approvedQty = Number(spec.approved_unit_qty ?? 0);
        const woApprovedQty = Number(spec.wo_approved_unit_qty ?? 0);
        const authUnits = (isNaN(approvedQty) ? 0 : approvedQty) +
          (isNaN(woApprovedQty) ? 0 : woApprovedQty);
        if (authUnits !== 0) {
          row.unitsAuthorized.calendarYear += authUnits;
          row.unitsAuthorized.fiscalYear += authUnits;
        }
      }

      const accUnits = Number(at.units ?? 0);
      if (!Number.isNaN(accUnits) && accUnits !== 0) {
        row.unitsAccomplished.calendarYear += accUnits;
        row.unitsAccomplished.fiscalYear += accUnits;
      }
    }

    return Array.from(map.values()).sort((a, b) =>
      a.treatmentName.localeCompare(b.treatmentName)
    );
  }

  private getFiscalYear(d: Date): number {
    const year = d.getFullYear();
    const month = d.getMonth();
    return month >= 9 ? year + 1 : year;
  }

  formatUnit(unitStr: string) {
    return unitStr.replace('Cost/', '');
  }

  private csvEscape(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    const str = String(value);

    if (/[",\r\n]/.test(str)) {
      return `"${str.replace(/"/g, '""')}"`;
    }

    return str;
  }

  exportToCsv() {
    this.dialog.open(ExportAuthActivitiesComponent, {
      width: "783px",
      panelClass: ['animate__animated', 'animate__fadeInDown'],
      data: {
        calendarYears: this.calendarYears,
        fiscalYears: this.fiscalYears,
        regions: this.regions,
        forestNames: this.forestNames,
        treatmentOptions: this.treatmentOptions,
      }
    }).afterClosed().subscribe((filters) => {
      this.downloadCsv(filters)
    });
  }

  downloadCsv(filters: Filters): void {
    const rows: BaerTreatmentRow[] = this.filterAccomplishmentTreatments(
      this.allTreatments, filters
    ) ?? [];

    if (!rows.length) {
      return;
    }

    const header = [
      'Treatment Option Id',
      'Treatment Code',
      'Treatment Name',
      'Unit of Measure',
      'Units Authorized (Calendar Year)',
      'Units Authorized (Fiscal Year)',
      'Units Accomplished (Calendar Year)',
      'Units Accomplished (Fiscal Year)',
    ];

    const lines: string[] = [];
    lines.push(header.map(h => this.csvEscape(h)).join(','));

    for (const row of rows) {
      const lineValues = [
        row.treatmentOptionId,
        row.treatmentCode,
        row.treatmentName,
        row.unitOfMeasure,
        row.unitsAuthorized?.calendarYear ?? 0,
        row.unitsAuthorized?.fiscalYear ?? 0,
        row.unitsAccomplished?.calendarYear ?? 0,
        row.unitsAccomplished?.fiscalYear ?? 0,
      ];

      lines.push(lineValues.map(v => this.csvEscape(v)).join(','));
    }

    const csvContent = lines.join('\r\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;',
    });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `baer-authorized-activities-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;

    a.click();
    window.URL.revokeObjectURL(url);
  }

  loadMore() {
    this.summaryData.data = [...this.allSummaryDataRows];
  }

  protected readonly faFileLines = faFileLines;
  protected readonly faDownload = faDownload;
}
